import { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Crosshair, ArrowRight } from "@phosphor-icons/react";

export default function Login({ mode = "login" }) {
  const isSignup = mode === "signup";
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user !== false) {
    const dest = location.state?.from?.pathname || "/";
    return <Navigate to={dest} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const res = isSignup
      ? await register(email, password, name)
      : await login(email, password);
    setBusy(false);
    if (!res.ok) setErr(res.error || "Failed");
    else navigate("/");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050505]" data-testid="auth-page">
      {/* Left visual */}
      <div className="hidden lg:block relative overflow-hidden border-r border-[#2a2a2a]">
        <div
          className="absolute inset-0 grid-bg opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-transparent to-[#050505]" />
        <div className="relative h-full p-12 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3 mb-12">
              <div className="w-10 h-10 border border-[#ffb000] flex items-center justify-center text-[#ffb000]">
                <Crosshair size={22} weight="bold" />
              </div>
              <div>
                <div className="text-2xl font-bold tracking-tight">LeadMe<span className="text-[#ffb000]">.ca</span></div>
                <div className="text-[10px] font-mono text-[#888] uppercase tracking-[0.2em]">v.2026.intelligence</div>
              </div>
            </div>

            <h1 className="text-5xl xl:text-6xl font-bold tracking-tighter leading-[0.95] mb-6">
              Find people who<br/>
              <span className="text-[#ffb000]">need your help.</span>
            </h1>
            <p className="text-[#888] text-base max-w-md leading-relaxed">
              Ethical, criteria-driven lead discovery across Reddit, Stack Exchange, Hacker News
              and 200+ free communities. Not spam — targeted conversations where your product
              actually solves a problem.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
            {[
              { v: "24/7", k: "AUTONOMOUS" },
              { v: "200+", k: "SOURCES" },
              { v: "0.6+", k: "CONFIDENCE" },
            ].map((s) => (
              <div key={s.k} className="bg-[#050505] p-5">
                <div className="text-3xl font-bold text-[#ededed] font-mono">{s.v}</div>
                <div className="text-[10px] font-mono text-[#888] tracking-[0.2em] mt-1">{s.k}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12 relative">
        <div className="absolute top-6 right-6 text-[10px] font-mono text-[#555] tracking-[0.2em] uppercase">
          <span className="pulse-dot inline-block w-1.5 h-1.5 bg-[#00ff66] mr-2" />
          SECURE / SSL
        </div>
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid={isSignup ? "signup-form" : "login-form"}>
          <div className="mb-8">
            <div className="text-[10px] font-mono text-[#ffb000] uppercase tracking-[0.3em] mb-3">
              [{isSignup ? "REGISTER" : "AUTHENTICATE"}]
            </div>
            <h2 className="text-3xl font-bold tracking-tight">
              {isSignup ? "Create your operator account" : "Sign in to your console"}
            </h2>
            <p className="text-[#888] text-sm mt-2 font-mono">
              {isSignup ? "// new operator setup" : "// secure session required"}
            </p>
          </div>

          {isSignup && (
            <div className="mb-5">
              <label className="field-label">Name</label>
              <input
                data-testid="signup-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="field-input"
                placeholder="Operator name"
              />
            </div>
          )}

          <div className="mb-5">
            <label className="field-label">Email</label>
            <input
              data-testid="auth-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field-input"
              placeholder="you@domain.com"
            />
          </div>

          <div className="mb-5">
            <label className="field-label">Password</label>
            <input
              data-testid="auth-password-input"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field-input"
              placeholder="••••••••"
            />
          </div>

          {err && (
            <div data-testid="auth-error" className="mb-5 p-3 border border-[#ff3b30]/40 bg-[#ff3b30]/10 text-[#ff3b30] text-xs font-mono">
              ERROR: {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid={isSignup ? "signup-submit-btn" : "login-submit-btn"}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {busy ? "PROCESSING…" : (isSignup ? "CREATE ACCOUNT" : "ENTER CONSOLE")}
            <ArrowRight size={14} weight="bold" />
          </button>

          <div className="mt-8 pt-6 border-t border-[#1a1a1a] text-center">
            <div className="text-xs text-[#888] font-mono">
              {isSignup ? "Already have access?" : "No account yet?"}{" "}
              <Link
                data-testid={isSignup ? "go-to-login-link" : "go-to-signup-link"}
                to={isSignup ? "/login" : "/signup"}
                className="text-[#ffb000] hover:text-[#ffc133]"
              >
                {isSignup ? "Sign in" : "Create one"}
              </Link>
            </div>
            <div className="mt-3 text-[10px] text-[#555] font-mono uppercase tracking-[0.2em]">
              demo: admin@leadme.ca / LeadMe2026!
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
